import { lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import AnnotationWorkspaceGuard from '../guards/AnnotationWorkspaceGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
import { PATH_DASHBOARD } from './paths';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const MCPAuthorize = Loadable(lazy(() => import('../pages/auth/MCPAuthorize')));

// RESTAURANT PRODUCTS
const RestaurantProductList = Loadable(lazy(() => import('../pages/dashboard/RestaurantProducts')));
const RestaurantProductCard = Loadable(lazy(() => import('../pages/dashboard/RestaurantProductsCard')));
const RestaurantProductCreate = Loadable(lazy(() => import('../pages/dashboard/RestaurantProductCreate')));
const MenusList = Loadable(lazy(() => import('../pages/dashboard/GeneralMenus')));
const MenusCard = Loadable(lazy(() => import('../pages/dashboard/MenusCard')));
const HealthMenus = Loadable(lazy(() => import('../pages/dashboard/HealthMenusList')));
const HealthMenuManagement = Loadable(lazy(() => import('../pages/dashboard/HealthMenusCategories')));
const CiqualItemsList = Loadable(lazy(() => import('../pages/dashboard/CiqualItemsList')));
const CiqualItemCreate = Loadable(lazy(() => import('../pages/dashboard/CiqualItemCreate')));
const CiqualItemDetails = Loadable(lazy(() => import('../pages/dashboard/CiqualItemDetails')));
const DishNutritionProfilesList = Loadable(lazy(() => import('../pages/dashboard/DishNutritionProfilesList')));
const DishNutritionProfileDetails = Loadable(lazy(() => import('../pages/dashboard/DishNutritionProfileDetails')));
const HealthFilesImport = Loadable(lazy(() => import('../pages/dashboard/HealthFilesImport')));
const HealthDocumentsStatus = Loadable(lazy(() => import('../pages/dashboard/HealthDocumentsStatus')));
const HealthMenuPromptEditor = Loadable(lazy(() => import('../pages/dashboard/HealthMenuPromptEditor')));
const ReportingDaily = Loadable(lazy(() => import('../pages/dashboard/ReportingDaily')));
const ReportingSummaryCsi = Loadable(lazy(() => import('../pages/dashboard/ReportingSummaryCsi')));

const ProductCatalog = Loadable(lazy(() => import('../pages/dashboard/ProductCatalog')));
const TaxonomyExplorer = Loadable(lazy(() => import('../pages/dashboard/TaxonomyExplorer')));

// TOOLS
const VisualizeAnnotation = Loadable(lazy(() => import('../pages/dashboard/VisualizeAnnotation')));
const VisualizeEnroll = Loadable(lazy(() => import('../pages/dashboard/VisualizeEnroll')));
const LegacyAnnotationTool = Loadable(lazy(() => import('../pages/dashboard/LegacyAnnotationTool')));
const AnnotationWorkspace = Loadable(lazy(() => import('../pages/dashboard/AnnotationWorkspace')));
const AnnotationBatches = Loadable(lazy(() => import('../pages/dashboard/AnnotationBatches')));
const AnnotationsImagesCheck = Loadable(lazy(() => import('../pages/dashboard/AnnotationsImagesCheck')));
const PipelineComparisons = Loadable(lazy(() => import('../pages/dashboard/PipelineComparisons')));
const AnnotationsCrop = Loadable(lazy(() => import('../pages/dashboard/AnnotationsCropTool')));
const IngredientAnnotationsImagesCheck = Loadable(
  lazy(() => import('../pages/dashboard/IngredientAnnotationsImagesCheck'))
);
const IngredientAnnotationsCrop = Loadable(lazy(() => import('../pages/dashboard/IngredientAnnotationsCropTool')));
const APISandbox = Loadable(lazy(() => import('../pages/dashboard/APISandbox')));
const ProductList = Loadable(lazy(() => import('../pages/dashboard/ProductList')));
const ProductCreate = Loadable(lazy(() => import('../pages/dashboard/ProductCreate')));
const ProductMerge = Loadable(lazy(() => import('../pages/dashboard/ProductMerge')));
const IngredientProductMappingsList = Loadable(lazy(() => import('../pages/dashboard/IngredientProductMappingsList')));
const ModelFeatureClfDetails = Loadable(lazy(() => import('../pages/dashboard/ModelFeatureClfDetails')));
const ApiDocumentation = Loadable(lazy(() => import('../pages/dashboard/ApiDocumentation')));

// HEALTH
const VisualizeHealthAnnotation = Loadable(lazy(() => import('../pages/dashboard/VisualizeHealthAnnotation')));
const Clients = Loadable(lazy(() => import('../pages/dashboard/Clients')));
const HealthAnnotationsImages = Loadable(lazy(() => import('../pages/dashboard/HealthAnnotationsImages')));
const DevicesList = Loadable(lazy(() => import('../pages/dashboard/DevicesList')));
const DevicesCreate = Loadable(lazy(() => import('../pages/dashboard/DevicesCreate')));
const DeviceProfilesList = Loadable(lazy(() => import('../pages/dashboard/DeviceProfilesList')));
const DeviceProfilesCreate = Loadable(lazy(() => import('../pages/dashboard/DeviceProfilesCreate')));

// ADMIN
const IdentificationToken = Loadable(lazy(() => import('../pages/dashboard/IdentificationTokenCreate')));
const RestaurantList = Loadable(lazy(() => import('../pages/dashboard/RestaurantList')));
const RestaurantCreate = Loadable(lazy(() => import('../pages/dashboard/RestaurantCreate')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));

// OTHER
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));

export default function Router() {
  const routes = useRoutes([
    { path: 'mcp/authorize', element: <MCPAuthorize /> },
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'taxonomy', element: <TaxonomyExplorer /> },
        // Once the dashboard as been created, the element should become <GeneralApp> again

        // --- General --- //
        {
          path: 'product-catalog',
          children: [
            { index: true, element: <Navigate to={PATH_DASHBOARD.productCatalog.category('indus_food')} replace /> },
            { path: ':objectClass', element: <ProductCatalog /> },
          ],
        },
        { path: 'app', element: <DashboardLayout /> },
        {
          path: 'restaurant_product',
          children: [
            {
              element: <Navigate to="/dashboard/restaurant_product/list" replace />,
              index: true,
            },
            { path: 'list', element: <RestaurantProductList /> },
            { path: 'card', element: <RestaurantProductCard /> },
            { path: 'new', element: <RestaurantProductCreate /> },
            { path: ':id/edit', element: <RestaurantProductCreate /> },
          ],
        },
        {
          path: 'menus',
          children: [
            {
              element: <Navigate to="/dashboard/menus/list" replace />,
              index: true,
            },
            { path: 'list', element: <MenusList /> },
            { path: 'card', element: <MenusCard /> },
          ],
        },
        {
          path: 'visualization',
          children: [
            {
              element: <Navigate to="/dashboard/visualization/analysis" replace />,
              index: true,
            },
            { path: 'analysis', element: <VisualizeAnnotation /> },
            { path: 'enrollment', element: <VisualizeEnroll /> },
          ],
        },
        {
          path: 'reporting',
          children: [
            {
              element: <Navigate to="/dashboard/reporting/daily" replace />,
              index: true,
            },
            { path: 'daily', element: <ReportingDaily /> },
            { path: 'summary/csi', element: <ReportingSummaryCsi /> },
          ],
        },

        // --- Health --- //
        {
          path: 'health_menu',
          children: [
            {
              element: <Navigate to="/dashboard/health_menu/list" replace />,
              index: true,
            },
            { path: 'list', element: <HealthMenus /> },
            { path: 'categories', element: <HealthMenuManagement /> },
            {
              path: 'ciqual',
              children: [
                { element: <Navigate to="/dashboard/health_menu/ciqual/list" replace />, index: true },
                { path: 'list', element: <CiqualItemsList /> },
                { path: 'new', element: <CiqualItemCreate /> },
                { path: ':id', element: <CiqualItemDetails /> },
                { path: ':id/edit', element: <CiqualItemCreate /> },
              ],
            },
            {
              path: 'dish-profiles',
              children: [
                { element: <Navigate to="/dashboard/health_menu/dish-profiles/list" replace />, index: true },
                { path: 'list', element: <DishNutritionProfilesList /> },
                { path: ':id', element: <DishNutritionProfileDetails /> },
              ],
            },
            {
              path: 'files',
              children: [
                { element: <Navigate to="/dashboard/health_menu/files/import" replace />, index: true },
                { path: 'import', element: <HealthFilesImport /> },
                { path: 'status', element: <HealthDocumentsStatus /> },
                { path: 'prompt-editor', element: <HealthMenuPromptEditor /> },
              ],
            },
          ],
        },
        {
          path: 'health_visualization',
          children: [
            {
              element: <Navigate to="/dashboard/health_visualization/analysis" replace />,
              index: true,
            },
            { path: 'analysis', element: <VisualizeHealthAnnotation /> },
          ],
        },
        {
          path: 'health_annotations',
          children: [
            {
              element: <Navigate to="/dashboard/health_annotations/images" replace />,
              index: true,
            },
            { path: 'images', element: <HealthAnnotationsImages /> },
          ],
        },
        {
          path: 'clients',
          children: [
            {
              element: <Navigate to="/dashboard/clients/list" replace />,
              index: true,
            },
            { path: 'list', element: <Clients /> },
          ],
        },
        {
          path: 'devices',
          children: [
            {
              element: <Navigate to="/dashboard/devices/list" replace />,
              index: true,
            },
            { path: 'list', element: <DevicesList /> },
            { path: 'new', element: <DevicesCreate /> },
            { path: ':id/edit', element: <DevicesCreate /> },
            {
              path: 'profiles',
              children: [
                {
                  element: <Navigate to="/dashboard/devices/profiles/list" replace />,
                  index: true,
                },
                { path: 'list', element: <DeviceProfilesList /> },
                { path: 'new', element: <DeviceProfilesCreate /> },
                { path: ':id/edit', element: <DeviceProfilesCreate /> },
              ],
            },
          ],
        },

        // --- Tools --- //
        {
          path: 'product',
          children: [
            {
              element: <Navigate to="/dashboard/product/list" replace />,
              index: true,
            },
            { path: 'list', element: <ProductList /> },
            { path: 'new', element: <ProductCreate /> },
            { path: ':id/edit', element: <ProductCreate /> },
            { path: ':id/edit/merged', element: <ProductCreate /> },
            { path: ':ids/merge', element: <ProductMerge /> },
            {
              path: 'ingredients-mapping',
              children: [
                {
                  element: <Navigate to="/dashboard/product/ingredients-mapping/list" replace />,
                  index: true,
                },
                { path: 'list', element: <IngredientProductMappingsList /> },
              ],
            },
          ],
        },
        {
          path: 'annotations',
          children: [
            {
              element: <Navigate to={PATH_DASHBOARD.annotations.legacy} replace />,
              index: true,
            },
            { path: 'images', element: <LegacyAnnotationTool /> },
            { path: 'workspace', element: <AnnotationWorkspace /> },
            { path: 'batches', element: <AnnotationBatches /> },
            { path: 'images-v2', element: <AnnotationWorkspace /> },
            { path: 'images-legacy', element: <LegacyAnnotationTool /> },
            {
              path: 'images-legacy/direct_link/:annotation',
              element: <LegacyAnnotationTool />,
            },
            {
              path: 'images/direct_link/:annotation',
              element: <LegacyAnnotationTool />,
            },
            { path: 'check', element: <AnnotationsImagesCheck /> },
            { path: 'crop', element: <AnnotationsCrop /> },
            {
              path: 'detection-comparisons',
              element: <Navigate to={PATH_DASHBOARD.evaluation.pipelineComparisons} replace />,
            },
            { path: 'ingredients/check', element: <IngredientAnnotationsImagesCheck /> },
            { path: 'ingredients/crop', element: <IngredientAnnotationsCrop /> },
          ],
        },
        {
          path: 'evaluation',
          children: [
            { index: true, element: <Navigate to={PATH_DASHBOARD.evaluation.pipelineComparisons} replace /> },
            { path: 'pipeline-comparisons', element: <PipelineComparisons /> },
            {
              path: 'detection-comparisons',
              element: <Navigate to={PATH_DASHBOARD.evaluation.pipelineComparisons} replace />,
            },
          ],
        },
        { path: 'api-sandbox', element: <APISandbox /> },
        { path: 'crop', element: <AnnotationsCrop /> },
        {
          path: 'models',
          children: [
            {
              element: <Navigate to="/dashboard/models/feature-clf" replace />,
              index: true,
            },
            { path: 'feature-clf', element: <ModelFeatureClfDetails /> },
          ],
        },
        { path: 'doc', element: <ApiDocumentation /> },

        // --- Admin --- //
        {
          path: 'restaurant',
          children: [
            {
              element: <Navigate to="/dashboard/restaurant/list" replace />,
              index: true,
            },
            { path: 'list', element: <RestaurantList /> },
            { path: 'new', element: <RestaurantCreate /> },
            { path: ':id/edit', element: <RestaurantCreate /> },
          ],
        },
        {
          path: 'user',
          children: [
            {
              element: <Navigate to="/dashboard/user/list" replace />,
              index: true,
            },
            { path: 'list', element: <UserList /> },
            { path: 'new', element: <UserCreate /> },
            { path: ':id/edit', element: <UserCreate /> },
          ],
        },
        { path: 'identification-token', element: <IdentificationToken /> },
      ],
    },
    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        // { path: 'coming-soon', element: <ComingSoon /> },
        // { path: 'maintenance', element: <Maintenance /> },
        // { path: 'pricing', element: <Pricing /> },
        // { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <Navigate to={PATH_AFTER_LOGIN} replace />,
      index: true,
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);

  return <AnnotationWorkspaceGuard>{routes}</AnnotationWorkspaceGuard>;
}
